import React from "react";
import Reveal from "./ui/Reveal";
import SectionLabel from "./ui/SectionLabel";
import SpotlightCard from "./ui/SpotlightCard";
import MagneticButton from "./ui/MagneticButton";
import PawField from "./ui/PawField";
import useTilt from "../hooks/useTilt";
import useCountUp from "../hooks/useCountUp";

const WHATSAPP_URL =
  "https://wa.me/5516997502577?text=Ol%C3%A1%2C%20quero%20saber%20mais%20sobre%20o%20Portal%20do%20Tutor%20do%20PawSuite.";

const CAPABILITIES = [
  {
    title: "Relatórios em PDF",
    text: "Faturas e relatórios de desempenho gerados com um clique, prontos para enviar.",
    d: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  },
  {
    title: "Gestão de equipe",
    text: "Permissões por função e registro de quem realizou cada serviço, sempre rastreável.",
    d: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
  },
  {
    title: "Histórico de pagamentos",
    text: "Fluxo de caixa simplificado, com todas as transações e métodos registrados.",
    d: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
  },
];

/** Número que sobe do zero quando entra na tela. */
const Stat = ({ value, suffix = "", label, decimals = 0 }) => {
  const [ref, current] = useCountUp(value, { decimals });
  return (
    <div ref={ref} className="text-center sm:text-left">
      <p className="font-display text-4xl font-extrabold text-white sm:text-5xl">
        {decimals ? current.toFixed(decimals) : Math.round(current)}
        <span className="text-tealGlow">{suffix}</span>
      </p>
      <p className="mt-1.5 text-sm text-white/45">{label}</p>
    </div>
  );
};

const Admin = () => {
  const tilt = useTilt({ max: 5, scale: 1.01 });

  return (
    <>
      {/* ================= Portal do Tutor ================= */}
      <section className="grain relative overflow-hidden bg-abyss py-28 text-white">
        <div className="pointer-events-none absolute inset-0 bg-grid-dark bg-grid [mask-image:radial-gradient(60%_60%_at_50%_50%,#000,transparent)]" />
        <PawField count={8} tone="rgba(255,255,255,.035)" />
        <div
          className="aurora-blob animate-drift"
          style={{
            top: "10%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "60vw",
            height: "34vw",
            background:
              "radial-gradient(circle,rgba(0,178,169,.32),transparent 65%)",
          }}
        />

        <div className="relative mx-auto max-w-5xl px-6 text-center">
          <Reveal>
            <SectionLabel tone="light">Portal do Tutor</SectionLabel>
          </Reveal>

          <Reveal delay={90}>
            <h2 className="mb-6 mt-7 font-display text-[clamp(2rem,4.6vw,3.4rem)] font-extrabold leading-[1.08]">
              Seu cliente acompanha tudo,
              <br className="hidden sm:block" /> sem te ligar
            </h2>
          </Reveal>

          <Reveal delay={170}>
            <p className="mx-auto mb-14 max-w-2xl text-lg leading-relaxed text-white/55">
              Transparência vira fidelização. No portal exclusivo, os tutores
              veem agendamentos, histórico de saúde e recebem as fotos do banho e
              tosa assim que o serviço termina.
            </p>
          </Reveal>

          <Reveal delay={250}>
            <div className="mb-16 grid grid-cols-2 gap-8 border-y border-white/10 py-10 sm:grid-cols-4">
              <Stat value={24} suffix="/7" label="Acesso do tutor" />
              <Stat value={3} suffix=" cliques" label="Para enviar uma foto" />
              <Stat value={100} suffix="%" label="Na nuvem" />
              <Stat value={0} suffix=" planilhas" label="Necessárias" />
            </div>
          </Reveal>

          <Reveal delay={330}>
            <MagneticButton
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group rounded-2xl bg-white px-8 py-4 text-lg font-bold text-midnight transition-all duration-300 hover:bg-tealVitality hover:text-white"
            >
              Saiba mais sobre o Portal
              <svg
                className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </MagneticButton>
          </Reveal>
        </div>
      </section>

      {/* ================= Painel do gestor ================= */}
      <section id="admin" className="relative overflow-hidden bg-accentWarm py-28">
        <div className="pointer-events-none absolute inset-0 dot-grid opacity-40 [mask-image:radial-gradient(70%_60%_at_50%_50%,#000,transparent)]" />

        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal variant="scale">
            <SpotlightCard
              spot="rgba(0,178,169,.10)"
              line="rgba(0,178,169,.5)"
              className="overflow-hidden rounded-[40px] border border-white bg-white shadow-[0_50px_100px_-40px_rgba(15,31,61,.4)]"
            >
              <div className="grid lg:grid-cols-12">
                {/* Lista de capacidades */}
                <div className="p-10 lg:col-span-5 lg:p-16">
                  <SectionLabel tone="amber">Gestão</SectionLabel>

                  <h2 className="mb-8 mt-6 font-display text-[clamp(1.75rem,3.4vw,2.6rem)] font-extrabold leading-[1.1] text-midnight">
                    Controle total do seu negócio
                  </h2>

                  <div className="space-y-2">
                    {CAPABILITIES.map((cap, i) => (
                      <Reveal key={cap.title} delay={i * 110}>
                        <div className="group flex gap-5 rounded-2xl p-4 transition-colors duration-300 hover:bg-softIvory">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-midnight/[.06] text-midnight transition-all duration-500 group-hover:-rotate-6 group-hover:bg-midnight group-hover:text-white">
                            <svg
                              className="h-6 w-6"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.9"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d={cap.d}
                              />
                            </svg>
                          </div>
                          <div>
                            <h4 className="mb-1 font-display text-lg font-bold text-midnight">
                              {cap.title}
                            </h4>
                            <p className="leading-relaxed text-midnight/55">
                              {cap.text}
                            </p>
                          </div>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                <div className="relative flex items-center justify-center border-t border-midnight/[.06] bg-gradient-to-br from-softIvory to-accentWarm/60 p-10 lg:col-span-7 lg:border-l lg:border-t-0 lg:p-14">
                  <div
                    className="tilt-scene w-full max-w-xl"
                    onMouseMove={tilt.onMouseMove}
                    onMouseLeave={tilt.onMouseLeave}
                    data-purpose="admin-screen-preview"
                  >
                    <div ref={tilt.ref} className="tilt-target">
                      <img
                        src="/tela_admin.png"
                        alt="Painel administrativo do PawSuite"
                        loading="lazy"
                        className="w-full rounded-2xl shadow-[0_35px_70px_-25px_rgba(15,31,61,.4)] ring-1 ring-midnight/[.06]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </SpotlightCard>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Admin;
